import { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Handshake, ArrowLeft, ArrowRight, Upload, CheckCircle2, MapPin, Store, Building2, Camera, FileCheck2, Phone, User, Lock, Locate } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import api, { errorMessage } from "@/lib/api";
import useLocations from "@/hooks/useLocations";
import useBranches from "@/hooks/useBranches";

const OFFICE_TYPES = ["Own Shop", "Rented Shop", "Finance Office", "Xerox Shop + Finance"];
const EXP_TYPES = ["DSA", "Connector", "Agent"];

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export default function PartnerRegisterPage() {
  const nav = useNavigate();
  const { districts } = useLocations();
  const [f, setF] = useState({
    name: "", phone: "", district: "", district_id: "", branch_id: "",
    experience_type: "", office_name: "", office_address: "", office_type: "",
    aadhaar_last4: "", years_in_finance: "", password: "",
    office_lat: null, office_lng: null,
  });
  const [photoB64, setPhotoB64] = useState(null);
  const [proofB64, setProofB64] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [pinning, setPinning] = useState(false);

  const pinLocation = () => {
    if (!navigator.geolocation) return toast.error("Geolocation not supported. Enter lat/lng manually.");
    setPinning(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setF(p => ({ ...p, office_lat: +pos.coords.latitude.toFixed(6), office_lng: +pos.coords.longitude.toFixed(6) }));
        setPinning(false);
        toast.success("Office GPS pinned");
      },
      (err) => { setPinning(false); toast.error("Location blocked. Enter lat/lng manually below."); },
      { enableHighAccuracy: true, timeout: 12000 }
    );
  };

  const distObj = useMemo(() => districts.find(d => d.name === f.district), [districts, f.district]);
  const { branches } = useBranches(distObj?.id);

  const setDistrict = (name) => {
    const obj = districts.find(d => d.name === name);
    setF({ ...f, district: name, district_id: obj?.id || "", branch_id: "" });
  };

  const pick = (setter) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) return toast.error("File too large (max 4MB)");
    const b64 = await fileToBase64(file);
    setter(b64);
  };

  const submit = async () => {
    if (!f.name || f.phone.length !== 10 || !f.district || !f.branch_id || !f.experience_type ||
        !f.office_name || !f.office_address || !f.office_type || !photoB64 || !proofB64 || !f.password) {
      return toast.error("Fill all mandatory fields, upload office photo + proof, and set a password");
    }
    if (f.office_lat === null || f.office_lng === null) {
      return toast.error("Pin your Office GPS location (mandatory).");
    }
    if (f.password.length < 4) return toast.error("Password must be at least 4 characters");
    setSubmitting(true);
    try {
      await api.post("/public/partner/register", {
        name: f.name, phone: f.phone, district: f.district, branch_id: f.branch_id,
        experience_type: f.experience_type, office_name: f.office_name,
        office_address: f.office_address, office_type: f.office_type,
        office_photo_b64: photoB64, office_proof_b64: proofB64,
        office_lat: f.office_lat, office_lng: f.office_lng,
        aadhaar_last4: f.aadhaar_last4, years_in_finance: parseInt(f.years_in_finance || "0"),
        password: f.password,
      });
      setSubmitted(true);
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setSubmitting(false); }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#0F172A] flex items-center justify-center px-6 py-12">
        <div className="max-w-lg w-full rounded-2xl border-2 border-[#22C55E]/50 p-8 text-center" style={{background:"linear-gradient(140deg,#052e18,#0a4023)"}} data-testid="partner-reg-success">
          <CheckCircle2 className="w-14 h-14 text-emerald-400 mx-auto mb-3"/>
          <div className="font-heading text-2xl text-white mb-2">Application Received</div>
          <div className="text-emerald-300 text-sm mb-4">Office verification pending.</div>
          <div className="text-slate-300 text-sm">Admin will verify within 24 hours. Your <b className="text-[#D4AF37]">Partner ID</b> will be generated after approval, and you can login using your phone + password.</div>
          <Button onClick={() => nav("/login")} className="mt-6 h-11 px-6 bg-[#F97316] hover:bg-[#ea6a10] text-white font-bold rounded-full">Back to Login</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0F172A] py-8 px-4">
      <Link to="/login" className="inline-flex items-center gap-2 text-slate-400 hover:text-amber-400 mb-4">
        <ArrowLeft className="w-4 h-4"/> Back to Login
      </Link>
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-6">
          <div className="inline-flex w-14 h-14 rounded-2xl bg-[#D4AF37]/20 border border-[#D4AF37]/50 items-center justify-center text-[#D4AF37] mb-3">
            <Handshake className="w-6 h-6"/>
          </div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#F97316] font-bold mb-1">Become a Partner</div>
          <h1 className="font-heading text-3xl text-white">DSA · Connector · Agent</h1>
          <p className="text-sm text-slate-400 mt-2">Earn 1% commission on every disbursed loan — unlimited, no cap.</p>
        </div>

        <div className="rounded-2xl border-2 border-[#F97316]/40 bg-[#F97316]/5 p-3 mb-5 text-center text-[11px] text-[#FDB979] font-semibold" data-testid="office-mandatory-note">
          Only applicants with a <b>physical office</b> will be approved for the Authorized Partner board.
        </div>

        <div className="rounded-2xl bg-[#141A2E] border border-[#1E2742] p-5 space-y-4">
          <FormRow label="Full Name *" testId="pr-name">
            <Input value={f.name} onChange={e=>setF({...f,name:e.target.value})} className="bg-slate-950 border-slate-700 h-11" placeholder="As per PAN/Aadhaar" data-testid="pr-name-input"/>
          </FormRow>
          <FormRow label="Phone (10 digits) *" testId="pr-phone">
            <Input inputMode="numeric" maxLength={10} value={f.phone} onChange={e=>setF({...f,phone:e.target.value.replace(/\D/g,"")})} className="bg-slate-950 border-slate-700 h-11 font-mono-alt" placeholder="Your login number" data-testid="pr-phone-input"/>
          </FormRow>

          <div className="grid grid-cols-2 gap-3">
            <FormRow label="District *" testId="pr-district">
              <Select value={f.district} onValueChange={setDistrict}>
                <SelectTrigger className="bg-slate-950 border-slate-700 h-11" data-testid="pr-district-trigger"><SelectValue placeholder="Select"/></SelectTrigger>
                <SelectContent className="bg-slate-900 text-white border-slate-700 max-h-64">
                  {districts.map(d => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </FormRow>
            <FormRow label="Branch *" testId="pr-branch">
              <Select value={f.branch_id} onValueChange={v => setF({...f,branch_id:v})} disabled={!distObj}>
                <SelectTrigger className="bg-slate-950 border-slate-700 h-11" data-testid="pr-branch-trigger"><SelectValue placeholder={distObj ? "Select branch" : "Pick district first"}/></SelectTrigger>
                <SelectContent className="bg-slate-900 text-white border-slate-700 max-h-64">
                  {branches.length === 0 ? <div className="p-3 text-sm text-slate-400">No branches. Ask admin.</div> :
                    branches.map(b => <SelectItem key={b.id} value={b.id}>{b.branch_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </FormRow>
          </div>

          <FormRow label="Experience Type *" testId="pr-exp">
            <Select value={f.experience_type} onValueChange={v=>setF({...f,experience_type:v})}>
              <SelectTrigger className="bg-slate-950 border-slate-700 h-11" data-testid="pr-exp-trigger"><SelectValue placeholder="DSA / Connector / Agent"/></SelectTrigger>
              <SelectContent className="bg-slate-900 text-white border-slate-700">
                {EXP_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </FormRow>

          <FormRow label="Office / Shop Name *" testId="pr-office">
            <Input value={f.office_name} onChange={e=>setF({...f,office_name:e.target.value})} className="bg-slate-950 border-slate-700 h-11" placeholder="e.g., Kumaran Finance & Xerox" data-testid="pr-office-input"/>
          </FormRow>

          <FormRow label="Office Full Address (with Pincode) *" testId="pr-addr">
            <Textarea value={f.office_address} onChange={e=>setF({...f,office_address:e.target.value})} className="bg-slate-950 border-slate-700" placeholder="Building, Street, Landmark, City, Pincode" rows={2} data-testid="pr-addr-input"/>
          </FormRow>

          {/* NEW: Office GPS Pin — mandatory */}
          <div data-testid="pr-office-gps-row">
            <Label className="text-[10px] uppercase tracking-widest text-slate-400 font-bold flex items-center gap-1.5">
              <MapPin className="w-3 h-3 text-[#D4AF37]"/> Office GPS Location *
              <span className="text-slate-600 text-[9px] normal-case ml-auto">Pin your shop on the map</span>
            </Label>
            <div className="mt-1.5">
              <Button
                type="button"
                data-testid="pr-pin-location"
                onClick={pinLocation}
                disabled={pinning}
                className={`w-full h-11 rounded-full font-bold text-sm ${f.office_lat!==null ? "bg-emerald-500 hover:bg-emerald-400 text-slate-900" : "bg-[#F97316] hover:bg-[#ea6a10] text-white"}`}
              >
                <Locate className="w-4 h-4 mr-2"/>
                {pinning ? "Getting GPS…" : (f.office_lat !== null ? `Pinned · ${f.office_lat}, ${f.office_lng}` : "📍 Pin My Office Location")}
              </Button>
              {f.office_lat !== null && (
                <>
                  <div className="mt-2 rounded-lg overflow-hidden border border-emerald-500/40" data-testid="pr-gps-preview">
                    <iframe
                      title="Office location preview"
                      className="w-full h-48"
                      src={`https://maps.google.com/maps?q=${f.office_lat},${f.office_lng}&z=16&output=embed`}
                      loading="lazy"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <Input data-testid="pr-lat" value={f.office_lat} onChange={e=>setF({...f, office_lat: e.target.value===""?null:parseFloat(e.target.value)})} placeholder="Latitude" className="bg-slate-950 border-slate-700 h-10 font-mono-alt text-xs"/>
                    <Input data-testid="pr-lng" value={f.office_lng} onChange={e=>setF({...f, office_lng: e.target.value===""?null:parseFloat(e.target.value)})} placeholder="Longitude" className="bg-slate-950 border-slate-700 h-10 font-mono-alt text-xs"/>
                  </div>
                  <button type="button" onClick={()=>setF({...f, office_lat:null, office_lng:null})} className="mt-1 text-[10px] text-red-400 hover:underline">Clear pin</button>
                </>
              )}
              {f.office_lat === null && (
                <div className="mt-2 text-[10px] text-slate-500">If GPS is blocked, tap the button then enter Lat / Lng manually below (or drop a pin at maps.google.com and paste values).</div>
              )}
            </div>
          </div>

          <FormRow label="Office Type *" testId="pr-office-type">
            <Select value={f.office_type} onValueChange={v=>setF({...f,office_type:v})}>
              <SelectTrigger className="bg-slate-950 border-slate-700 h-11" data-testid="pr-office-type-trigger"><SelectValue placeholder="Select"/></SelectTrigger>
              <SelectContent className="bg-slate-900 text-white border-slate-700">
                {OFFICE_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </FormRow>

          <FileUpload label="Office Photo (Shop Front) *" hint="Clear front view of your shop" b64={photoB64} onPick={pick(setPhotoB64)} onClear={()=>setPhotoB64(null)} testId="pr-photo"/>
          <FileUpload label="Office Proof (EB Bill / Rent / GST / Business Card) *" hint="Any document proving the office" b64={proofB64} onPick={pick(setProofB64)} onClear={()=>setProofB64(null)} testId="pr-proof"/>

          <div className="grid grid-cols-2 gap-3">
            <FormRow label="Aadhaar (Last 4)" testId="pr-aadhaar">
              <Input inputMode="numeric" maxLength={4} value={f.aadhaar_last4} onChange={e=>setF({...f,aadhaar_last4:e.target.value.replace(/\D/g,"")})} className="bg-slate-950 border-slate-700 h-11 font-mono-alt" placeholder="XXXX" data-testid="pr-aadhaar-input"/>
            </FormRow>
            <FormRow label="Years in Finance" testId="pr-years">
              <Input inputMode="numeric" value={f.years_in_finance} onChange={e=>setF({...f,years_in_finance:e.target.value.replace(/\D/g,"")})} className="bg-slate-950 border-slate-700 h-11 font-mono-alt" placeholder="0" data-testid="pr-years-input"/>
            </FormRow>
          </div>

          <FormRow label="Set Login Password *" testId="pr-password">
            <Input type="password" value={f.password} onChange={e=>setF({...f,password:e.target.value})} className="bg-slate-950 border-slate-700 h-11" placeholder="At least 4 characters" data-testid="pr-password-input"/>
          </FormRow>

          <Button
            data-testid="pr-submit"
            disabled={submitting}
            onClick={submit}
            className="w-full h-12 bg-[#F97316] hover:bg-[#ea6a10] text-white font-black rounded-full text-sm shadow-lg shadow-orange-500/25"
          >
            {submitting ? "Submitting…" : "Submit for Office Verification"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function FormRow({ label, children, testId }) {
  return (
    <div data-testid={testId}>
      <Label className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function FileUpload({ label, hint, b64, onPick, onClear, testId }) {
  const ref = useRef();
  return (
    <div data-testid={testId}>
      <Label className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">{label}</Label>
      <div className="mt-1">
        {b64 ? (
          <div className="relative rounded-lg border border-emerald-500/40 bg-emerald-500/5 p-2 flex items-center gap-2">
            <img src={b64} alt="preview" className="w-14 h-14 rounded object-cover"/>
            <div className="flex-1 min-w-0 text-xs">
              <div className="text-emerald-400 font-bold">Uploaded ✓</div>
              <div className="text-slate-500 text-[10px]">{hint}</div>
            </div>
            <button type="button" onClick={onClear} className="text-red-400 text-xs hover:underline">Replace</button>
          </div>
        ) : (
          <button
            type="button"
            onClick={()=>ref.current?.click()}
            className="w-full rounded-lg border-2 border-dashed border-slate-700 hover:border-amber-500/60 hover:bg-amber-500/5 p-4 text-center text-slate-400"
            data-testid={`${testId}-picker`}
          >
            <Upload className="w-5 h-5 mx-auto mb-1 text-slate-500"/>
            <div className="text-xs font-semibold">Tap to upload</div>
            <div className="text-[10px] text-slate-600 mt-0.5">{hint}</div>
          </button>
        )}
        <input ref={ref} type="file" accept="image/*" className="hidden" onChange={onPick} data-testid={`${testId}-input`}/>
      </div>
    </div>
  );
}
